import React, { useState, useRef, useEffect } from 'react';
import { siteData } from '../data/mock';

const RoleTooltip = ({ role, isAbove, isHovered, alignment }) => {
  // alignment: 'center' | 'left' | 'right' — controls horizontal anchor
  const getHorizontalStyle = () => {
    if (alignment === 'right') {
      // Anchor right edge to align with content border
      return { right: '-20px', transform: `translateY(${isAbove ? '-16px' : '16px'}) ${isHovered ? 'scale(1)' : 'scale(0.92)'}` };
    }
    if (alignment === 'left') {
      return { left: '0', transform: `translateY(${isAbove ? '-16px' : '16px'}) ${isHovered ? 'scale(1)' : 'scale(0.92)'}` };
    }
    return { left: '50%', transform: `translateX(-50%) translateY(${isAbove ? '-16px' : '16px'}) ${isHovered ? 'scale(1)' : 'scale(0.92)'}` };
  };

  const horizontalStyle = getHorizontalStyle();

  return (
    <div
      style={{
        position: 'absolute',
        [isAbove ? 'bottom' : 'top']: '100%',
        ...horizontalStyle,
        opacity: isHovered ? 1 : 0,
        pointerEvents: isHovered ? 'auto' : 'none',
        background: '#1a1a1a',
        border: '1px solid #FF98BF',
        borderRadius: '12px',
        padding: '16px 20px',
        width: '260px',
        zIndex: 50,
        transition: 'opacity 0.25s cubic-bezier(0.25,0.46,0.45,0.94), transform 0.25s cubic-bezier(0.25,0.46,0.45,0.94)',
        boxShadow: '0 8px 32px rgba(255,152,191,0.15), 0 2px 8px rgba(0,0,0,0.3)',
      }}
    >
      <p
        style={{
          fontFamily: 'Coolvetica, sans-serif',
          fontSize: '16px',
          fontWeight: 400,
          color: '#FF98BF',
          letterSpacing: '-0.025em',
          lineHeight: '18px',
          marginBottom: '8px',
        }}
      >
        {role.name}
      </p>
      <p
        style={{
          fontFamily: 'InterVariable, sans-serif',
          fontSize: '13px',
          fontWeight: 400,
          fontStyle: 'italic',
          color: '#ddd',
          lineHeight: '18px',
        }}
      >
        {role.description}
      </p>
    </div>
  );
};

const TimelineNode = ({ role, index, total, isCompact, isSelected, onSelect }) => {
  const [isHovered, setIsHovered] = useState(false);
  const isAbove = role.position === 'top';
  const active = isCompact ? isSelected : isHovered;

  // Smart tooltip alignment to prevent overflow
  let alignment = 'center';
  if (index >= total - 1) alignment = 'right';
  if (index === 0) alignment = 'left';

  return (
    <div
      style={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        cursor: 'pointer',
        scrollSnapAlign: 'center',
      }}
      onMouseEnter={() => { if (!isCompact) setIsHovered(true); }}
      onMouseLeave={() => { if (!isCompact) setIsHovered(false); }}
      onClick={() => onSelect?.(index)}
    >
      {/* Label above the line */}
      {isAbove && (
        <span
          style={{
            fontFamily: 'Coolvetica, sans-serif',
            fontSize: isCompact ? '20px' : '24px',
            fontWeight: 400,
            color: active ? '#FF98BF' : '#fff',
            letterSpacing: '-0.025em',
            lineHeight: '28px',
            marginBottom: '8px',
            transition: 'color 0.2s ease, transform 0.2s ease',
            transform: active ? 'translateY(-3px)' : 'translateY(0)',
            whiteSpace: 'nowrap',
          }}
        >
          {role.name}
        </span>
      )}

      {/* Vertical tick mark — crosses through the horizontal line */}
      <div
        style={{
          width: '1.5px',
          height: isCompact ? '36px' : '40px',
          background: active ? '#FF98BF' : '#c0c5ce',
          transition: 'background 0.2s ease',
        }}
      />

      {/* Label below the line */}
      {!isAbove && (
        <span
          style={{
            fontFamily: 'Coolvetica, sans-serif',
            fontSize: isCompact ? '20px' : '24px',
            fontWeight: 400,
            color: active ? '#FF98BF' : '#fff',
            letterSpacing: '-0.025em',
            lineHeight: '28px',
            marginTop: '8px',
            transition: 'color 0.2s ease, transform 0.2s ease',
            transform: active ? 'translateY(3px)' : 'translateY(0)',
            whiteSpace: 'nowrap',
          }}
        >
          {role.name}
        </span>
      )}

      {/* Tooltip popup — anchored to the label area */}
      {!isCompact && (
        <div
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            [isAbove ? 'top' : 'bottom']: 0,
          }}
        >
          <RoleTooltip
            role={role}
            isAbove={isAbove}
            isHovered={isHovered}
            alignment={alignment}
          />
        </div>
      )}
    </div>
  );
};

const ForSection = () => {
  const data = siteData.forSection;
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isCompact, setIsCompact] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.2 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 740px)');
    const onChange = () => setIsCompact(mq.matches);
    onChange();
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  useEffect(() => {
    if (!isCompact) return;
    const el = scrollRef.current;
    if (!el) return;

    const update = () => {
      const max = el.scrollWidth - el.clientWidth;
      setCanScrollLeft(el.scrollLeft > 2);
      setCanScrollRight(el.scrollLeft < max - 2);
    };

    update();
    el.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    return () => {
      el.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, [isCompact]);

  const selectedRole = data.roles[selectedIndex] || data.roles[0];

  return (
    <section
      ref={sectionRef}
      data-section="for-section"
      className="relative w-full"
      style={{
        background: '#000',
        padding: 'clamp(52px, 8vw, 80px) clamp(16px, 5.5vw, 60px) clamp(72px, 9vw, 100px)',
        overflow: 'visible',
      }}
    >
      {/* Main heading */}
      <h2
        style={{
          fontFamily: 'Coolvetica, sans-serif',
          fontSize: 'clamp(40px, 7vw, 64px)',
          fontWeight: 400,
          letterSpacing: '-0.025em',
          lineHeight: 'clamp(44px, 7.6vw, 70px)',
          whiteSpace: 'normal',
          opacity: isVisible ? 1 : 0,
          transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
          transition: 'opacity 0.6s ease, transform 0.6s ease',
        }}
      >
        <span style={{ color: '#fff' }}>{data.heading} </span>
        <span style={{ color: '#FF98BF' }}>{data.headingHighlight}</span>
      </h2>
      <h2
        style={{
          fontFamily: 'Coolvetica, sans-serif',
          fontSize: 'clamp(40px, 7vw, 64px)',
          fontWeight: 400,
          letterSpacing: '-0.025em',
          lineHeight: 'clamp(44px, 7.6vw, 70px)',
          color: '#fff',
          whiteSpace: 'normal',
          opacity: isVisible ? 1 : 0,
          transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
          transition: 'opacity 0.6s ease 0.05s, transform 0.6s ease 0.05s',
        }}
      >
        {data.headingEnd}
      </h2>

      {/* Subtitle */}
      <p
        style={{
          fontFamily: 'InterVariable, sans-serif',
          fontSize: '16px',
          fontWeight: 400,
          fontStyle: 'italic',
          color: '#fff',
          lineHeight: '19px',
          marginTop: '16px',
          maxWidth: '915px',
          opacity: isVisible ? 1 : 0,
          transform: isVisible ? 'translateY(0)' : 'translateY(15px)',
          transition: 'opacity 0.6s ease 0.15s, transform 0.6s ease 0.15s',
        }}
      >
        {data.subtitle}
      </p>

      {/* Mobile: detail card sits above the timeline rail */}
      {isCompact && selectedRole && (
        <div className="for-mobileCard">
          <div className="for-mobileCardInner">
            <div className="for-mobileCardTitle">{selectedRole.name}</div>
            <div className="for-mobileCardBody">{selectedRole.description}</div>
          </div>
        </div>
      )}

      {/* Timeline */}
      <div
        className="for-timeline"
        style={{
          position: 'relative',
          marginTop: '100px',
          padding: '60px 0',
          opacity: isVisible ? 1 : 0,
          transition: 'opacity 0.6s ease 0.3s',
        }}
      >
        <div ref={scrollRef} className="for-timelineScroll">
          <div className="for-rail">
            {/* Horizontal line — spans the full rail width (extends to last node) */}
            <div
              className="for-line"
              style={{
                position: 'absolute',
                top: '50%',
                left: 0,
                right: 0,
                height: '1.5px',
                background:
                  'linear-gradient(90deg, #FF98BF 0%, #c0c5ce 30%, #c0c5ce 70%, #FF98BF 100%)',
                transform: 'translateY(-50%)',
                zIndex: 1,
              }}
            />

            {/* Nodes container */}
            <div
              className="for-nodes"
              style={{
                position: 'relative',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                paddingLeft: '40px',
                paddingRight: '40px',
                zIndex: 2,
              }}
            >
              {data.roles.map((role, i) => (
                <TimelineNode
                  key={i}
                  role={role}
                  index={i}
                  total={data.roles.length}
                  isCompact={isCompact}
                  isSelected={i === selectedIndex}
                  onSelect={setSelectedIndex}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Mobile: scroll hint button + fade to suggest more content */}
        {isCompact && canScrollLeft && (
          <button
            type="button"
            className="for-scrollArrow for-scrollArrowLeft"
            onClick={() => scrollRef.current?.scrollBy({ left: -260, behavior: 'smooth' })}
            aria-label="Scroll timeline left"
          >
            ‹
          </button>
        )}
        {isCompact && canScrollRight && (
          <button
            type="button"
            className="for-scrollArrow for-scrollArrowRight"
            onClick={() => scrollRef.current?.scrollBy({ left: 260, behavior: 'smooth' })}
            aria-label="Scroll timeline right"
          >
            ›
          </button>
        )}
      </div>
    </section>
  );
};

export default ForSection;
